import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent, DxDataGridModule, DxTemplateModule } from 'devextreme-angular';
import { Subject, takeUntil } from 'rxjs';

import { DevextremesharedmoduleModule } from '@app/shared/Module/devextremeshared/devextremeshared.module';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
import { LanguageService } from '@app/services/language.service';
import { ScreenSizeService } from '@app/services/screensize.service';
import { DashboardFilterService } from '@app/services/dashboard_filter_dependent_multiselection.service';
import { ServerCommunicatorService } from '@app/services/server-communicator.service';
import { SUBMENU_ID } from '@app/classes/domain/constants';
import { FromEntityTypes } from '@app/classes/infrastructure/enums';
import { TransportData } from '@app/classes/infrastructure/transportdata';
import { PayloadPacketFacade } from '@app/classes/infrastructure/payloadpacket/payloadpacketfacade';
import { TodaysTaskDetailsFetchRequest } from '@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklistdetailsfetchrequest';
import { TodaysTaskDetails } from '@app/classes/domain/entities/website/dashboard/todaystasklist/todaystasklistdetails';

export interface TodayTaskDetailRow {
  id: number | string;
  beneficiary_name: string;
  beneficiary_name_mr: string;
  house_no: string;
  family_id: string;
  mobile_no: string;
  visit: string;
  visit_number: number;
  is_extra_visit: boolean;
  visit_raw: string;
}

// task id (from the list page) -> register to fetch from
const TASK_ENTITY_MAP: Record<string, FromEntityTypes> = {
  pregnant: FromEntityTypes.maata_register_master,
  anc: FromEntityTypes.maata_register_master,
  immunization: FromEntityTypes.child_immunization_register_master,
  hbnc: FromEntityTypes.hbnc_follow_up_master,
  pnc: FromEntityTypes.pnc_follow_up_master,
  abortion: FromEntityTypes.abortion_follow_up_master,
  miscarriage: FromEntityTypes.miscarriage_follow_up_master,
};

// register (FromEntityTypes) -> route to open when "Do It" is clicked
const ENTITY_ROUTE_MAP: Record<number, string> = {
  [FromEntityTypes.anc_register_master]: SUBMENU_ID.ANC_REGISTER_FOLLOW_UP,
  [FromEntityTypes.anc_follow_up_master]: SUBMENU_ID.ANC_REGISTER_FOLLOW_UP,
  [FromEntityTypes.maata_register_master]: SUBMENU_ID.ANC_REGISTER_FOLLOW_UP,
  [FromEntityTypes.child_immunization_register_master]: SUBMENU_ID.CHILD_IMMUNIZATION,
  [FromEntityTypes.hbnc_follow_up_master]: SUBMENU_ID.HBNC_REGISTER,
  [FromEntityTypes.pnc_follow_up_master]: SUBMENU_ID.PNC_REGISTER,
  [FromEntityTypes.abortion_follow_up_master]: SUBMENU_ID.ABORTION_REGISTER,
  [FromEntityTypes.miscarriage_follow_up_master]: SUBMENU_ID.MISCARRIAGE_REGISTER,
};

@Component({
  selector: 'app-todays-task-list-details',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, DevextremesharedmoduleModule, DxDataGridModule, DxTemplateModule],
  templateUrl: './todays-task-list-details.component.html',
  styleUrl: './todays-task-list-details.component.scss',
})
export class TodaysTaskListDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('dataGrid', { static: false }) dataGrid!: DxDataGridComponent;

  private destroy$ = new Subject<void>();

  taskId: string = '';
  taskTitle: string = '';
  taskTitleMr: string = '';
  fromEntityType: number = 0;

  pageSize: number = 0;
  pageIndex: number = 0;

  searchText: string = '';
  isLoading: boolean = false;

  tableData: TodayTaskDetailRow[] = [];
  filteredData: TodayTaskDetailRow[] = [];

  constructor(
    private router: Router,
    public langService: LanguageService,
    private translate: TranslateService,
    private screenSizeService: ScreenSizeService,
    private dashboardFilterService: DashboardFilterService,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private breadcrumbService: BreadcrumbService
  ) { }

  async ngOnInit(): Promise<void> {
    this.pageSize = this.screenSizeService.getPageSize('withDropdown', 2);

    const nav = this.readNav();
    if (!nav?.from_entity_type) {
      await this.router.navigate([SUBMENU_ID.TODAYS_TASK_LIST]);
      return;
    }

    // Persist so refresh works seamlessly
    sessionStorage.setItem('todaysTaskDetailsNav', JSON.stringify(nav));

    this.taskId = nav.taskId || '';
    this.taskTitle = nav.title || '';
    this.taskTitleMr = nav.titleMr || '';
    this.fromEntityType = Number(nav.from_entity_type) || 0;

    this.updateBreadcrumb();

    await this.loadTaskDetails();

    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateBreadcrumb();
      this.tableData = this.tableData.map((r) => ({ ...r, visit: this.formatVisit(r.visit_raw) }));
      this.applyFilter();
    });
  }

  private updateBreadcrumb(): void {
    const title = this.langService.currentLang === 'mr' ? (this.taskTitleMr || this.taskTitle) : this.taskTitle;
    const detailSuffix = this.langService.currentLang === 'mr' ? 'तपशील' : 'Details';
    const detailTitle = title ? `${title} ${detailSuffix}` : (this.langService.currentLang === 'mr' ? 'कामाचे तपशील' : 'Task Details');

    this.breadcrumbService.set([
      { label: 'Home', labelKey: 'MENU.Dashboard', path: SUBMENU_ID.HOME },
      { label: "Today's Tasks", labelKey: 'TODAYS_TASK.ALL_TASKS_TODAY', path: SUBMENU_ID.TODAYS_TASK_LIST },
      { label: detailTitle },
    ]);
  }

  private readNav(): any {
    try {
      const stored = sessionStorage.getItem('todaysTaskDetailsNav');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (err) {
      console.error('[TodaysTaskDetails] Error parsing nav data from sessionStorage', err);
    }

    const st = history.state;
    if (st?.from_entity_type) return st;

    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------------- Titles ----------------
  private get isDose(): boolean {
    return this.fromEntityType === FromEntityTypes.child_immunization_register_master;
  }
  get pageTitleLine1(): string {
    return this.langService.currentLang === 'mr' ? (this.taskTitleMr || this.taskTitle) : this.taskTitle;
  }

  get pageTitleLine2(): string {
    const isMr = this.langService.currentLang === 'mr';
    return this.isDose ? (isMr ? 'डोस तपशील' : 'Dose Details') : (isMr ? 'भेट तपशील' : 'Visit Details');
  }

  get searchPlaceholder(): string {
    return 'Search here......';
  }

  getColCaption(field: string): string {
    const isMr = this.langService.currentLang === 'mr';
    switch (field) {
      case 'sr_no': return isMr ? 'अ.क्र.' : 'Sr.No';
      case 'beneficiary_name': return isMr ? 'लाभार्थ्याचे नाव' : 'Beneficiary Name';
      case 'house_no': return isMr ? 'घर क्र.' : 'House No';
      case 'family_id': return isMr ? 'कुटुंब आयडी' : 'Family ID';
      case 'mobile_no': return isMr ? 'मोबाईल क्र.' : 'Mobile No';
      case 'visit': return this.isDose ? (isMr ? 'डोस' : 'Dose') : (isMr ? 'भेट' : 'Visit');
      case 'action': return isMr ? 'कृती' : 'Action';
      default: return field;
    }
  }

  private formatVisit(raw: string): string {
    const v = (raw ?? '').toString().trim();
    const m = v.match(/^(\d+)\s*([a-zA-Z]+)$/);
    if (!m) return v;                      // "1", "2", or any other text -> as sent

    const n = Number(m[1]);
    const u = m[2].toLowerCase();
    const isMr = this.langService.currentLang === 'mr';
    const one = n <= 1;

    if (u.startsWith('d')) return `${n} ${isMr ? 'दिवस' : (one ? 'Day' : 'Days')}`;
    if (u.startsWith('w')) return `${n} ${isMr ? (one ? 'आठवडा' : 'आठवडे') : (one ? 'Week' : 'Weeks')}`;
    if (u.startsWith('m')) return `${n} ${isMr ? (one ? 'महिना' : 'महिने') : (one ? 'Month' : 'Months')}`;
    if (u.startsWith('y')) return `${n} ${isMr ? (one ? 'वर्ष' : 'वर्षे') : (one ? 'Year' : 'Years')}`;
    return v;
  }

  // ---------------- Fetch ----------------
  async loadTaskDetails(): Promise<void> {
    this.isLoading = true;
    try {
      if (!this.fromEntityType) {
        console.warn('[TodaysTaskDetails] Missing from_entity_type');
        this.setRows([]);
        return;
      }

      const req = new TodaysTaskDetailsFetchRequest();
      req.from_entity_type = this.fromEntityType;

      const f = this.dashboardFilterService;
      if (f.state_ids.length) req.state_ids = f.state_ids;
      if (f.zone_ids.length) req.zone_ids = f.zone_ids;
      if (f.district_ids.length) req.district_ids = f.district_ids;
      if (f.block_ids.length) req.block_ids = f.block_ids;
      if (f.phc_ids.length) req.phc_ids = f.phc_ids;
      if (f.sc_ids.length) req.sc_ids = f.sc_ids;
      if (f.village_ids.length) req.village_ids = f.village_ids;
      if (f.asha_ids.length) req.asha_employee_record_ids = f.asha_ids;

      const pkt = PayloadPacketFacade.GetInstance().CreateNewPayloadPacket2(req.FormulateTransportData());
      const tr = await ServerCommunicatorService.GetInstance().sendHttpRequest(pkt);

      if (!tr?.Successful) {
        this.setRows([]);
        return;
      }

      const rows = TodaysTaskDetails.FromTransportData(JSON.parse(tr.Tag) as TransportData);
      console.log("[TodaysTaskDetails] Fetched rows:", rows);

      this.setRows(
        rows.map((r) => ({
          id: r.id,
          beneficiary_name: r.name || "",
          beneficiary_name_mr: "",                    // API has no mr name yet
          house_no: r.house_no || "",
          family_id: r.family_serial_number || "",
          mobile_no: r.mobile || "",
          visit_number: parseInt(String(r.visit_no), 10) || 0,
          is_extra_visit: false,
          visit_raw: String(r.visit_no ?? ''),
          visit: this.formatVisit(String(r.visit_no ?? '')),
        }))
      );
    } catch (err) {
      console.warn('[TodaysTaskDetails] Fetch failed:', err);
      this.setRows([]);
    } finally {
      this.isLoading = false;
      this.cdr.markForCheck();
    }
  }

  // ---------------- Search ----------------
  onSearchInput(text: string): void {
    this.searchText = text || '';
    this.applyFilter();
  }

  applyFilter(): void {
    const term = (this.searchText || '').toLowerCase().trim();
    this.filteredData = !term
      ? [...this.tableData]
      : this.tableData.filter((r) =>
        [r.beneficiary_name, r.beneficiary_name_mr, r.house_no, r.family_id, r.mobile_no, r.visit]
          .some((v) => (v || '').toLowerCase().includes(term))
      );

    this.dataGrid?.instance?.pageIndex(0);
  }

  onCellPrepared(e: any): void {
    if (e.rowType === 'data' && e.column.dataField === 'sr_no') {
      e.cellElement.innerText = (e.component.pageIndex() * e.component.pageSize() + e.rowIndex + 1).toString();
    }
  }

  // ---------------- Navigation ----------------
  async onDoIt(row: TodayTaskDetailRow): Promise<void> {
    const route = ENTITY_ROUTE_MAP[this.fromEntityType];
    if (!route) {
      console.warn(`[TodaysTaskDetails] No route for from_entity_type ${this.fromEntityType}`);
      return;
    }

    const navState = { id: row.id, visit_no: row.visit_number, from: 'todays-task' };
    sessionStorage.setItem('todaysTaskActionData', JSON.stringify(navState));

    await this.router.navigate([route], {
      state: navState,
    });
  }

  private setRows(rows: TodayTaskDetailRow[]): void {
    this.tableData = rows;
    this.applyFilter();
  }

  visitSortValue = (row: TodayTaskDetailRow): number => {
    const m = (row.visit_raw || '').trim().match(/^(\d+)\s*([a-zA-Z]*)$/);
    if (!m) return Number.MAX_SAFE_INTEGER;
    const n = Number(m[1]);
    const u = m[2].toLowerCase();
    if (u.startsWith('d')) return n;
    if (u.startsWith('w')) return n * 7;
    if (u.startsWith('m')) return n * 30;
    if (u.startsWith('y')) return n * 365;
    return n;
  };

  async goBack(): Promise<void> {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      await this.router.navigate([SUBMENU_ID.TODAYS_TASK_LIST]);
    }
  }
}
